
import { mapGetters, mapActions } from 'vuex';
import { isMobileResponsive } from '~/utils/mixins';
import SuccessIcon from '@/components/ui/icons/SuccessIcon.vue';
import MyFormulateCheckbox from '~/components/ui/atom/vueFormulateCustoms/MyFormulateCheckbox.vue';

export default {
  name: 'PollVote',
  components: {
    SuccessIcon,
    MyFormulateCheckbox,
  },
  mixins: [isMobileResponsive],
  props: {
    pollId: {
      type: String,
      default: null,
    },
    mentions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      pollDefinition: {
        options: [],
      },
      selectedOption: [],
      hasVoted: false,
      showMessageAfterVoting: false,
      userVotedOptionId: null,
      mentionIds: [],
      voteResults: null,
      pollQuestion: '',
    };
  },
  computed: {
    ...mapGetters('authentication', ['profileId']),
    poll() {
      return this.$store.state.post.polls[this.pollId] || [];
    },
    totalVotes() {
      return this.pollDefinition.options.reduce(
        (sum, option) => sum + option.votes,
        0
      );
    },
    totalVoters() {
      if (!this.voteResults) return 0;
      return new Set(this.voteResults.map((r) => r.profileId)).size;
    },
    pollWithPercentage() {
      if (
        !this.pollDefinition?.options ||
        this.pollDefinition.options.length === 0
      ) {
        return [];
      }

      const mostVotedOptionIds = this.getOptionIdWithMostVotes();

      return this.pollDefinition.options.map((option) => {
        const percentage = this.totalVotes
          ? (option.votes / this.totalVotes) * 100
          : 0;
        return {
          ...option,
          percentage: percentage.toFixed(0),
          isMostVoted: mostVotedOptionIds.includes(option.id),
        };
      });
    },
    isMultipleChoice() {
      return this.poll?.some((p) => p.isMultipleChoice);
    },
    isVoteDisabled() {
      return !this.selectedOption || !this.selectedOption.length;
    },
  },
  async mounted() {
    if (this.pollId) {
      try {
        await this.getPoll({ id: this.pollId });
        this.voteResults = await this.getPollResults({ id: this.pollId });
        this.hasVoted =
          this.voteResults &&
          this.voteResults.find((r) => r.profileId === this.profileId);

        const currentPoll = this.poll?.filter((p) => p.pollId === this.pollId);

        this.pollDefinition = {
          options: currentPoll.map((p) => ({
            id: p.id,
            text: p.option,
            votes: p.votes,
          })),
        };
        this.pollQuestion = currentPoll.length ? currentPoll[0].title : '';
      } catch (error) {
        console.error('Error fetching poll data:', error);
      }
    }
  },
  methods: {
    ...mapActions('post', ['getPoll', 'getPollResults', 'votePoll']),
    async submitVote() {
      if (this.isVoteDisabled) return;

      const selectedIds = this.selectedOption;

      await Promise.all(selectedIds.map((id) => this.votePoll({ id })));

      this.hasVoted = true;
      this.showMessageAfterVoting = true;

      this.pollDefinition.options = this.pollDefinition.options.map((opt) => {
        if (selectedIds.includes(opt.id)) {
          return {
            ...opt,
            votes: opt.votes + 1,
          };
        }
        return opt;
      });

      this.voteResults = [
        ...(this.voteResults || []),
        ...selectedIds.map((id) => ({
          optionId: id,
          profileId: this.profileId,
        })),
      ];
    },
    getPercentage(votes) {
      return this.totalVotes ? ((votes / this.totalVotes) * 100).toFixed(0) : 0;
    },
    getOptionIdWithMostVotes() {
      const highestVotes = Math.max(
        ...this.pollDefinition.options.map((option) => option.votes)
      );
      return this.pollDefinition.options
        .filter((option) => option.votes === highestVotes)
        .map((option) => option.id);
    },
    hasVotedByOptionId(id) {
      return (
        this.voteResults?.some(
          (vr) => vr.optionId === id && vr.profileId === this.profileId
        ) ?? false
      );
    },
  },
};
